import { useCallback, useEffect, useState } from 'react'
import { notificationsApi } from '../lib/api'
import { disablePush, enablePush, getPushStatus, type PushStatus } from '../utils/push'

// Per-status copy in one table rather than a chain of ternaries in the JSX: there are
// six states and each needs its own dot colour, headline, explanation, and button
// label (or no button at all), which is more than nested ternaries read well for.
const STATUS_COPY: Record<
  PushStatus,
  { dot: string; label: string; description: string; action: 'enable' | 'disable' | null }
> = {
  granted: {
    dot: 'var(--bright-green)',
    label: 'Notifications are on',
    description:
      "You'll get a notification on this device when someone requests your item, approves a rental, sends a message, or an item is ready for pickup.",
    action: 'disable',
  },
  default: {
    dot: 'var(--text-muted)',
    label: 'Notifications are off',
    description:
      'Turn these on to hear about rental requests and approvals right away, instead of only when you next open the app or check your email.',
    action: 'enable',
  },
  denied: {
    dot: 'var(--error-text)',
    label: 'Notifications are blocked',
    description:
      "This browser is blocking notifications for igotthatus.com. We can't undo that from here — you'll need to allow notifications for this site in your browser settings, then come back to this page.",
    action: null,
  },
  'needs-install': {
    dot: '#F7A82F',
    label: 'Install the app first',
    description:
      'On iPhone and iPad, notifications only work once igotthatus.com is added to your Home Screen. Tap the Share button in Safari, choose "Add to Home Screen", then open igotthatus.com from there and come back to this page.',
    action: null,
  },
  unsupported: {
    dot: 'var(--text-muted)',
    label: 'Not available in this browser',
    description:
      "This browser doesn't support push notifications. You'll still get every notification by email and in the app.",
    action: null,
  },
  unconfigured: {
    dot: 'var(--text-muted)',
    label: 'Not available yet',
    description:
      "Push notifications aren't set up on this environment yet. You'll still get every notification by email and in the app.",
    action: null,
  },
}

export default function NotificationsTab({
  showSnackbar,
}: {
  showSnackbar: (message: string) => void
}) {
  const [status, setStatus] = useState<PushStatus | null>(null)
  const [isBusy, setIsBusy] = useState(false)

  const refresh = useCallback(async () => {
    setStatus(await getPushStatus())
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const handleToggle = async (action: 'enable' | 'disable') => {
    setIsBusy(true)
    try {
      if (action === 'enable') {
        const result = await enablePush()
        setStatus(result)
        showSnackbar(
          result === 'granted'
            ? 'Notifications enabled on this device'
            : result === 'denied'
              ? 'Notifications were blocked by your browser'
              : 'Notifications could not be enabled',
        )
      } else {
        await disablePush()
        await refresh()
        showSnackbar('Notifications turned off on this device')
      }
    } catch {
      showSnackbar('Something went wrong updating notifications')
    } finally {
      setIsBusy(false)
    }
  }

  const handleTest = async () => {
    setIsBusy(true)
    try {
      const res = await notificationsApi.sendTestPush()
      const delivered = res.data?.data?.delivered ?? 0
      // delivered === 0 is a real, actionable outcome, not an error: permission is
      // granted in this browser but the server has no live subscription for it (most
      // often because the browser's subscription was rotated or cleared).
      showSnackbar(
        delivered > 0
          ? 'Test notification sent — check your device'
          : 'No devices are currently subscribed. Try turning notifications off and on again.',
      )
    } catch {
      showSnackbar('Could not send a test notification')
    } finally {
      setIsBusy(false)
    }
  }

  if (status === null) {
    return <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--slate)' }}>Loading notification settings...</div>
  }

  const copy = STATUS_COPY[status]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <p style={{ margin: 0, color: 'var(--slate)', fontSize: '0.9rem' }}>
        Push notifications are per device — turning them on here affects this phone or browser only.
        Email notifications are always sent regardless of this setting.
      </p>

      <div style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: copy.dot }} />
          <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.95rem' }}>{copy.label}</span>
        </div>

        <p style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', color: 'var(--slate)', lineHeight: 1.5 }}>
          {copy.description}
        </p>

        {copy.action && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={() => handleToggle(copy.action!)}
              disabled={isBusy}
              style={{
                padding: '0.75rem 1rem',
                background: copy.action === 'enable' ? 'var(--forest-green)' : 'transparent',
                color: copy.action === 'enable' ? 'white' : 'var(--slate)',
                fontWeight: 600,
                fontSize: '0.95rem',
                borderRadius: '8px',
                border: copy.action === 'enable' ? 'none' : '1px solid var(--border)',
                cursor: isBusy ? 'not-allowed' : 'pointer',
                opacity: isBusy ? 0.6 : 1,
              }}
            >
              {copy.action === 'enable' ? 'Turn on notifications' : 'Turn off on this device'}
            </button>

            {status === 'granted' && (
              <button
                type="button"
                onClick={handleTest}
                disabled={isBusy}
                style={{
                  padding: '0.75rem 1rem',
                  background: 'transparent',
                  color: 'var(--forest-green)',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  borderRadius: '8px',
                  border: '1px solid var(--forest-green)',
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  opacity: isBusy ? 0.6 : 1,
                }}
              >
                Send a test notification
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
